/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react";

import FullPageLayout from "../../../templates/FullPageLayout";
import {
  Button,
  Card,
  Checkbox,
  Col,
  Flex,
  Form,
  Radio,
  RadioChangeEvent,
  Row,
  Slider,
  SliderSingleProps,
  Tooltip,
} from "antd";
import { InfoIconOutlined, NeedHelp } from "../../../atoms/CustomIcons";
import { DateField, InputField, SelectField } from "../../../atoms/FormFields";
import { Controller, useForm } from "react-hook-form";
import { useNavigate, useLocation } from "react-router-dom";
import { CheckboxGroupProps } from "antd/es/checkbox";
import { useAppDispatch, useAppSelector } from "../../../config/hooks";
import { getPotentialDealFormData } from "../ducks/actions";
import { createPotentialDeal } from "../ducks/services";
import { SuccessMessage } from "../../../atoms/Toasts/SuccessMessage";
import { AxiosError, AxiosResponse } from "axios";
import { ErrorMessage } from "../../../atoms/Toasts/ErrorMessage";
import dayjs from "dayjs";

interface FormValues {
  name: string;
  property_id: string;
  manager_id: string;
  sale_type: string;
  not_specified: boolean;
  start_date: string;
  end_date: string;
  net_terms: string;
  billing: string;
}

const billingForFixedPrice = [
  { value: "upfront_payment", label: "Upfront payment" },
  { value: "equal_monthly_payments", label: "Equal monthly payments" },
  { value: "per_service_completion", label: "Per service completion" },
  { value: "per_milestone", label: "Per milestone" },
  { value: "post_delivery", label: "Post-delivery" },
];

const billingForTM = [
  { value: "per_service_completion", label: "Per service completion" },
  { value: "monthly", label: "Monthly" },
  { value: "per_milestone", label: "Per milestone" },
  { value: "post_delivery", label: "Post-delivery" },
];

const saleTypeDropdown = [
  { value: "base_contract", label: "Base contract" },
  { value: "upsale", label: "Upsell" },
  { value: "one_off_deal", label: "One off deal" },
];

const netTermDropdown = [
  { value: "due_upon_receipt", label: "Due upon receipt" },
  { value: "net_15", label: "Net 15" },
  { value: "net_30", label: "Net 30" },
  { value: "net_45", label: "Net 45" },
  { value: "net_60", label: "Net 60" },
  { value: "net_90", label: "Net 90" },
];

interface PropertyOption {
  value: string;
  label: string;
  address: string;
  client_name: string;
  client_address: string;
  manager_id?: string;
}

export default function CreateNewDeal() {
  const location = useLocation();
  const templateTitle = location.state?.templateTitle;
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [sliderValue, setSliderValue] = useState(50);
  const [renewOption, setRenewOption] = useState("yes");
  const [selectedProperty, setSelectedProperty] =
    useState<PropertyOption | null>(null);
  const [pricingOption, setPricingOption] = useState("fixed_price");
  const [sliderDisabled, setSliderDisabled] = useState(false);
  const locationUrl = window.location.pathname;
  const pathSegments = locationUrl.split("/");
  const companyId = pathSegments[pathSegments.length - 1];
  const dealsFormData = useAppSelector(
    (state) => state?.potentialDeal?.dealFormData
  );

  console.log("dealsFormData", dealsFormData);

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormValues>();

  const startDate = watch("start_date");
  const endDate = watch("end_date");

  useEffect(() => {
    if (templateTitle) {
      setValue("name", templateTitle);
    }
  }, [templateTitle, setValue]);

  const onSubmit = (val: FormValues) => {
    setLoading(true);

    interface ManagerOption {
      value: string;
      label: string;
      image?: string;
    }
    const managerData = (
      dealsFormData?.managers as ManagerOption[] | undefined
    )?.find((manager) => manager?.value === val?.manager_id);

    const payload = {
      name: val?.name ? val?.name : undefined,
      property_id: val?.property_id ? val?.property_id : undefined,
      manager_id: val?.manager_id ? val?.manager_id : undefined,
      manager_name: managerData?.label ?? "",
      manager_image_url: managerData?.image ?? "",
      sale_type: val?.sale_type ? val?.sale_type : undefined,
      probability: sliderValue,
      not_specified: val?.not_specified,
      company_id: companyId,
      start_date: val?.start_date
        ? dayjs(val?.start_date).format("YYYY-MM-DD")
        : undefined,
      end_date: val?.end_date
        ? dayjs(val?.end_date).format("YYYY-MM-DD")
        : undefined,
      renewable: renewOption === "yes" ? true : false,
      pricing_model: pricingOption,
      billing: val?.billing ? val?.billing : undefined,
      net_terms: val?.net_terms ? val?.net_terms : undefined,
    };

    createPotentialDeal(payload)
      .then((res: AxiosResponse) => {
        setLoading(false);
        SuccessMessage("Deal created");
        setTimeout(() => {
          navigate(`/deals-detail/${res?.data?.id}/${companyId}`);
        }, 500);
      })
      .catch((e: unknown) => {
        if (e instanceof AxiosError) {
          setLoading(false);
          ErrorMessage(
            e?.response?.data?.error?.message ?? "Something went wrong"
          );
        } else {
          setLoading(false);
          ErrorMessage("Something went wrong");
        }
      });
  };

  const optionsPricing: CheckboxGroupProps<string>["options"] = [
    { label: "Fixed price", value: "fixed_price" },
    { label: "Time & material", value: "time_material" },
  ];

  const optionsRenewable: CheckboxGroupProps<string>["options"] = [
    { label: "Yes", value: "yes" },
    { label: "No", value: "no" },
  ];

  const marks: SliderSingleProps["marks"] = {
    0: "0%",
    25: "25%",
    50: "50%",
    75: "75%",
    100: "100%",
  };

  const onRenewChange = (e: RadioChangeEvent) => {
    setRenewOption(e.target.value);
  };
  const onPricingChange = (e: RadioChangeEvent) => {
    setPricingOption(e.target.value);
  };

  useEffect(() => {
    if (companyId) {
      dispatch(getPotentialDealFormData(companyId));
    }
  }, [companyId, dispatch]);

  // console.log("dealsFormData?.managers", dealsFormData?.managers);

  const onChange = (value: string | number | readonly string[]) => {
    const selectedProperty: any = dealsFormData?.property?.find(
      (property: PropertyOption) => property.value === value
    );
    setSelectedProperty(selectedProperty || null);
    if (selectedProperty && selectedProperty.manager_id) {
      setValue("manager_id", selectedProperty?.manager_id || "");
    } else {
      setValue("manager_id", "");
    }
  };

  const specifiedChange = (checked: boolean) => {
    if (checked) {
      setSliderValue(0);
      setSliderDisabled(true);
    } else {
      setSliderValue(0);
      setSliderDisabled(false);
    }
  };

  useEffect(() => {
    const storedPropertyId = localStorage.getItem("property_id_for_new_deal");
    if (storedPropertyId && dealsFormData?.property) {
      setValue("property_id", storedPropertyId);

      const selectedProperty = dealsFormData?.property?.find(
        (property: PropertyOption) => property.value === storedPropertyId
      );
      setSelectedProperty(selectedProperty || null);
      localStorage.removeItem("property_id_for_new_deal");
    }
  }, [setValue, dealsFormData]);

  useEffect(() => {
    const removeStoredClientId = () => {
      localStorage.removeItem("property_id_for_new_deal");
    };
    window.addEventListener("popstate", removeStoredClientId);
    return () => {
      window.removeEventListener("popstate", removeStoredClientId);
    };
  }, []);

  return (
    <FullPageLayout loading={loading}>
      <Form
        className="newDealForm"
        layout="vertical"
        onFinish={handleSubmit(onSubmit)}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.preventDefault();
        }}
      >
        <Button className="btn-sm fixedTopBtn">
          <NeedHelp /> Need help?
        </Button>

        <Row gutter={8} style={{ width: "672px" }}>
          <Col span={24}>
            <div
              className="title"
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
              }}
            >
              <div>
                <h2> New deal </h2>
                <p> Deals are potential contracts that you’re about to win. </p>
              </div>
            </div>
          </Col>
          <Col span={24}>
            <Card>
              <Row gutter={20}>
                <Col span={24} style={{ marginBottom: "20px" }}>
                  <h4> General details </h4>
                </Col>
                <Col span={24}>
                  <InputField
                    fieldName="name"
                    label="Deal name"
                    control={control}
                    iProps={{
                      placeholder: "Enter deal name",
                    }}
                    isRequired
                    rules={{
                      required: "Deal name is required.",
                    }}
                    validate={errors.name && "error"}
                    validMessage={errors.name && errors.name.message}
                    tooltipTitle="Deal name"
                  />

                  <SelectField
                    fieldName="property_id"
                    label="Property"
                    control={control}
                    selectOption={dealsFormData?.property ?? []}
                    iProps={{
                      placeholder: "Select property",
                    }}
                    isRequired
                    rules={{
                      required: "Property name is required.",
                    }}
                    validate={errors.property_id && "error"}
                    validMessage={
                      errors.property_id && errors.property_id.message
                    }
                    onSelectChange={onChange}
                    tooltipTitle="Property"
                  />
                  {selectedProperty && (
                    <Card className="propertyDetailsWrapper">
                      <Row gutter={[20, 20]}>
                        <Col span={24}>
                          <h2>{selectedProperty?.label}</h2>
                        </Col>
                        <Col span={12}>
                          <div className="propertyCol">
                            <label>Address:</label>
                            <p style={{ maxWidth: "50%" }}>
                              {selectedProperty.address}
                            </p>
                          </div>
                        </Col>
                        <Col span={12}>
                          <div className="propertyCol">
                            <label>Client:</label>
                            <p>
                              <span
                                style={{
                                  marginBottom: 4,
                                  fontSize: 14,
                                  lineHeight: "20px",
                                  color: "#151718",
                                  fontWeight: 400,
                                }}
                              >
                                {selectedProperty?.client_name ?? "N/A"}
                              </span>
                              <span>
                                {selectedProperty?.client_address ?? ""}
                              </span>
                            </p>
                          </div>
                        </Col>
                      </Row>
                    </Card>
                  )}
                </Col>
                <Col span={12}>
                  <SelectField
                    fieldName="manager_id"
                    label="Responsible manager"
                    control={control}
                    selectOption={dealsFormData?.managers ?? []}
                    iProps={{
                      placeholder: "Select manager",
                      allowClear: true,
                    }}
                    tooltipTitle="Responsible manager"
                    // isRequired
                    // rules={{
                    //   required: "Responsible manager is required.",
                    // }}
                    // validate={errors.manager_id && "error"}
                    // validMessage={
                    //   errors.manager_id && errors.manager_id.message
                    // }
                  />
                </Col>
                <Col span={12}>
                  <SelectField
                    fieldName="sale_type"
                    label="Type of sale"
                    control={control}
                    selectOption={saleTypeDropdown}
                    iProps={{
                      placeholder: "Select type of sale",
                      allowClear: true,
                    }}
                    tooltipTitle="Type of sale"
                  />
                </Col>
                <Col span={24} style={{ marginBottom: "20px" }}>
                  <div className="probabilityTitle">
                    <label>Probability</label>
                    <span className="not_specified">
                      <Controller
                        name="not_specified"
                        control={control}
                        render={({ field }) => (
                          <Checkbox
                            {...field}
                            checked={field.value}
                            onChange={(e) => {
                              field.onChange(e.target.checked);
                              specifiedChange(e.target.checked);
                            }}
                            style={{
                              fontWeight: "400",
                              fontSize: "12px",
                            }}
                          >
                            Not specified
                          </Checkbox>
                        )}
                      />
                    </span>
                  </div>
                  <Slider
                    marks={marks}
                    className="probabilitySlider"
                    value={sliderValue}
                    onChange={setSliderValue}
                    disabled={sliderDisabled}
                  />
                </Col>
              </Row>
            </Card>

            <Card>
              <Row gutter={20} style={{ marginBottom: "20px" }}>
                <Col span={24} style={{ marginBottom: "20px" }}>
                  <h4> Contract terms </h4>
                </Col>

                <Col span={12} className="mb-0">
                  <DateField
                    fieldName="start_date"
                    label="Start date"
                    control={control}
                    tooltipTitle="Start date"
                    iProps={{
                      format: [
                        "MM/DD/YYYY",
                        "M/D/YYYY",
                        "MMM D, YYYY",
                        "MM-DD-YYYY",
                        "MMDDYYYY",
                        "DDMMYYYY",
                        "DD/MM/YYYY",
                        "DD-MM-YYYY",
                        "MMM D YYYY",
                        "M-D-YYYY",
                        "D-M-YYYY",
                        "D/M/YYYY",
                        "MDYYYY",
                        "DMYYYY",
                      ],
                      placeholder: "MM/DD/YYYY",
                      disabledDate: (current: any) => {
                        if (!endDate || !current) return false;
                        return (
                          current.startOf("day").valueOf() >
                          dayjs(endDate).startOf("day").valueOf()
                        );
                      },
                    }}
                  />
                </Col>
                <Col span={12} className="mb-0">
                  <DateField
                    fieldName="end_date"
                    label="End date"
                    control={control}
                    tooltipTitle="End date"
                    iProps={{
                      format: [
                        "MM/DD/YYYY",
                        "M/D/YYYY",
                        "MMM D, YYYY",
                        "MM-DD-YYYY",
                        "MMDDYYYY",
                        "DDMMYYYY",
                        "DD/MM/YYYY",
                        "DD-MM-YYYY",
                        "MMM D YYYY",
                        "M-D-YYYY",
                        "D-M-YYYY",
                        "D/M/YYYY",
                        "MDYYYY",
                        "DMYYYY",
                      ],
                      placeholder: "MM/DD/YYYY",
                      disabledDate: (current: any) => {
                        if (!startDate || !current) return false;
                        return (
                          current.startOf("day").valueOf() <
                          dayjs(startDate).startOf("day").valueOf()
                        );
                      },
                    }}
                  />
                </Col>
              </Row>

              <Row
                gutter={20}
                style={{
                  backgroundColor: "#F6F9F9",
                  padding: "16px",
                  borderRadius: "16px",
                  marginLeft: "0px",
                  marginRight: "0px",
                }}
              >
                <Col span={12} style={{ marginBottom: "16px" }}>
                  <Flex
                    vertical
                    gap={8}
                    style={{
                      fontSize: "14px",
                      lineHeight: "20px",
                      fontWeight: 500,
                    }}
                  >
                    <div
                      style={{
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      Renewable
                      <Tooltip title="Renewable" className="cs-tooltip-icon">
                        <InfoIconOutlined />
                      </Tooltip>
                    </div>
                    <div className="potentialDealsRadio">
                      <Radio.Group
                        block
                        options={optionsRenewable}
                        value={renewOption}
                        onChange={onRenewChange}
                        optionType="button"
                        buttonStyle="solid"
                      />
                    </div>
                  </Flex>
                </Col>
                <Col span={12} style={{ marginBottom: "16px" }}>
                  <Flex
                    vertical
                    gap={8}
                    style={{
                      fontSize: "14px",
                      lineHeight: "20px",
                      fontWeight: 500,
                    }}
                  >
                    <div
                      style={{
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      Pricing model
                      <Tooltip
                        title="Pricing model"
                        className="cs-tooltip-icon"
                      >
                        <InfoIconOutlined />
                      </Tooltip>
                    </div>
                    <div className="potentialDealsRadio">
                      <Radio.Group
                        block
                        options={optionsPricing}
                        value={pricingOption}
                        onChange={onPricingChange}
                        optionType="button"
                        buttonStyle="solid"
                      />
                    </div>
                  </Flex>
                </Col>

                <Col span={12} className="mb-0">
                  <SelectField
                    fieldName="billing"
                    label="Billing"
                    control={control}
                    selectOption={
                      pricingOption === "fixed_price"
                        ? billingForFixedPrice
                        : billingForTM
                    }
                    iProps={{
                      placeholder: "Select billing type",
                    }}
                    tooltipTitle="Billing"
                  />
                </Col>
                <Col span={12} className="mb-0">
                  <SelectField
                    fieldName="net_terms"
                    label="Net terms"
                    control={control}
                    selectOption={netTermDropdown}
                    iProps={{
                      placeholder: "Select net terms",
                    }}
                    tooltipTitle="Net terms"
                  />
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>

        <div className="fixed_btm_card">
          <Card>
            <div className="centered">
              <Button
                htmlType="button"
                className="outlined-btn"
                onClick={() => navigate(`/potential-deals/${companyId}`)}
              >
                Cancel
              </Button>
              <div style={{ display: "flex", gap: "16px" }}>
                <Button htmlType="submit" type="primary" className="mint-btn">
                  Create deal
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </Form>
    </FullPageLayout>
  );
}
